import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import BIMViewer from './BIMViewer';
import { cdnAssetUrl, cdnFolder } from '../../utils/cdnAssets';
import styles from './DWGViewer.module.css';
import Icon from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';

const FILE_QUERY = gql`
  query BIMFileV2($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      extension
    }
  }
`;

type Stage = 'checking' | 'idle' | 'processing' | 'ready' | 'failed' | 'notprepared';

// Self-hosted BIM viewer — THE default 3D viewer (IFC → GLB via IfcOpenShell on
// our Batch light lane). IFC converts automatically at upload; RVT needs a paid
// one-time Autodesk RVT→IFC translation, so it is gated behind a button here
// (after that single step Autodesk is out of the loop — we mesh the IFC ourselves
// and both formats converge on 3d/<id>/model.glb). Mounted on two routes:
// /projects/:projectId/viewer-3d-v2/:fileId (authenticated) and
// /share/:token/viewer-3d/:fileId (public share link, no account).
// The legacy APS viewer stays at /projects/:projectId/viewer-3d/:fileId, URL-only.
interface BIMViewerV2Props {
  fileId?: string;
  embedded?: boolean;
}

const BIMViewerV2 = ({ fileId: propFileId, embedded }: BIMViewerV2Props = {}) => {
  const { fileId: urlFileId, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const fileId = propFileId ?? urlFileId;
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('checking');
  const [procStage, setProcStage] = useState('');
  const [error, setError] = useState('');
  // Bumped to restart the poll loop (after the convert button / Retry).
  const [pollTick, setPollTick] = useState(0);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    return { Authorization: `Bearer ${token}` };
  }, []);

  const { data } = useQuery(FILE_QUERY, { variables: { id: fileId }, skip: !fileId || isShare });
  // Share visitors have no account — file info comes from the public share-link query.
  const { data: shareData, error: shareError } = useQuery(SHARED_LINK_QUERY, {
    variables: { token: shareToken },
    skip: !isShare,
  });
  const fileInfo = isShare
    ? shareData?.sharedLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;
  const ext = (fileInfo?.extension || '').toLowerCase();
  const base = ext === 'rvt' ? 'rvt2ifc' : 'ifc3d';
  // Revoked/expired link, or a file that is not part of it — don't spin forever.
  const shareDead = isShare && (!!shareError || (!!shareData && !fileInfo));

  useEffect(() => {
    // rcs renders via the legacy APS viewer (see the early return below).
    if (!fileId || !ext || ext === 'rcs') return;
    let cancelled = false;
    let timer: number | undefined;
    const shareQS = isShare ? `?shareToken=${encodeURIComponent(shareToken!)}` : '';

    const poll = async (first: boolean) => {
      try {
        const res = await fetch(`/api/${base}/status/${fileId}${shareQS}`, isShare ? undefined : { headers: authHeader() });
        const st = (await res.json().catch(() => ({}))) as { status?: string; error?: string; stage?: string; active?: boolean };
        if (cancelled) return;
        if (!res.ok) throw new Error(st.error || 'Could not check conversion status');
        if (st.status === 'ready') { setStage('ready'); return; }
        // A failed IFC self-heals on the first authenticated poll: the translate
        // below clears the stale error and resubmits. RVT and share links show it.
        if (st.status === 'failed' && (isShare || ext === 'rvt' || !first)) {
          setStage('failed');
          setError(st.error || 'Conversion failed');
          return;
        }
        if (isShare) {
          // Share visitors can't start conversions. Wait while a job the owner
          // started is running; otherwise the owner must open the file once.
          // "Not prepared" still re-checks slowly — the in-flight flag is
          // in-memory on the backend, so a restart mid-conversion must not
          // strand the visitor on a dead screen.
          const running = ext === 'rvt' ? st.stage !== 'starting' : !!st.active;
          if (running) {
            setStage('processing');
            setProcStage(st.stage || '');
            timer = window.setTimeout(() => poll(false), 5000);
          } else {
            setStage('notprepared');
            timer = window.setTimeout(() => poll(false), 15000);
          }
          return;
        }
        if (ext === 'rvt') {
          // Never started → button-gated (the RVT→IFC step is a paid Autodesk
          // translation; it must not fire just because someone opened the page).
          // Keep a slow background check: 'starting' can be a transient stale
          // read while a conversion is actually under way.
          if (st.stage === 'starting') {
            setStage('idle');
            timer = window.setTimeout(() => poll(false), 15000);
            return;
          }
          setStage('processing');
          setProcStage(st.stage || '');
          timer = window.setTimeout(() => poll(false), 5000);
          return;
        }
        // IFC: make sure a job is running (idempotent — upload normally already
        // started it; a stale failure is cleared and resubmitted), then poll.
        if (first) {
          const t = await fetch(`/api/ifc3d/translate/${fileId}`, { method: 'POST', headers: authHeader() });
          const body = (await t.json().catch(() => ({}))) as { status?: string; error?: string };
          if (!t.ok) throw new Error(body.error || 'Could not start conversion');
          if (body.status === 'ready') { setStage('ready'); return; }
          setStage('processing');
          setProcStage('');
        }
        timer = window.setTimeout(() => poll(false), 5000);
      } catch (err) {
        if (!cancelled) {
          setStage('failed');
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };

    poll(true);
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [fileId, ext, base, isShare, shareToken, authHeader, pollTick]);

  // Convert button (RVT) and Retry (both formats): kick the pipeline, then let
  // the poll loop take over. The RVT translate request streams the model to
  // Autodesk inside the POST — minutes for big files — so 'processing' shows
  // for that whole window.
  const startConversion = async () => {
    setError('');
    setStage('processing');
    setProcStage(ext === 'rvt' ? 'uploading' : '');
    try {
      const t = await fetch(`/api/${base}/translate/${fileId}`, { method: 'POST', headers: authHeader() });
      const body = (await t.json().catch(() => null)) as { status?: string; error?: string } | null;
      if (body === null) {
        // Non-JSON response = the proxy timed out the request (nginx caps it at
        // 300 s) while the RVT→OSS stream keeps running on the backend. Not a
        // failure — fall back to polling; status reports the real state.
        if (ext === 'rvt') { setPollTick((t2) => t2 + 1); return; }
        throw new Error('Could not start conversion');
      }
      if (!t.ok) throw new Error(body.error || 'Could not start conversion');
      if (body.status === 'ready') { setStage('ready'); return; }
      setPollTick((t2) => t2 + 1);
    } catch (err) {
      // Network hiccup mid-upload: same story as the proxy timeout for RVT.
      if (ext === 'rvt' && err instanceof TypeError) { setPollTick((t2) => t2 + 1); return; }
      setStage('failed');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const jwtToken = localStorage.getItem(TOKEN_KEY) || '';
  // Prod: the GLB from CloudFront's nearest edge. Undefined while asking, so
  // the viewer is not started twice.
  const [cdnGlb, setCdnGlb] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (stage !== 'ready' || !fileId) return;
    let live = true;
    cdnFolder(fileId, isShare ? { shareToken } : { headers: { Authorization: `Bearer ${jwtToken}` } })
      .then((folder) => { if (live) setCdnGlb(folder ? cdnAssetUrl(folder, 'model.glb') : null); });
    return () => { live = false; };
  }, [stage, fileId, isShare, shareToken, jwtToken]);
  // Share visitors fetch the GLB with the share token instead of an account JWT.
  const glbAuth = cdnGlb
    ? `src=${encodeURIComponent(cdnGlb)}`
    : isShare
      ? `src=${encodeURIComponent(`/api/ifc3d/file/${fileId}?shareToken=${shareToken}`)}`
      : `token=${encodeURIComponent(jwtToken)}`;

  const procText =
    procStage === 'uploading' ? 'Uploading the model to Autodesk for the one-time RVT → IFC conversion…'
    : procStage === 'translating' ? 'Autodesk is converting the Revit model to IFC. This can take several minutes for large models.'
    : procStage === 'meshing' ? 'Meshing the model on our own conversion service…'
    : ext === 'rvt' ? 'Preparing the model…'
    : 'The model is being converted on our own conversion service. The first open takes a few minutes; afterwards it loads instantly.';

  // RCS has no self-hosted pipeline — old deep links (incl. share links created
  // under the APS flow) keep working through the legacy Autodesk viewer.
  if (ext === 'rcs') return embedded ? null : <BIMViewer />;

  if (shareDead) {
    return (
      <div className={styles.container}>
        <div className={styles.viewerBody}>
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <Icon name="link_off" style={{ fontSize: 40, color: 'rgba(248,113,113,0.7)' }} />
            </div>
            <p className={styles.overlayTitle}>Link Not Available</p>
            <p className={styles.overlayText}>This share link is invalid, expired, or does not include this file.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      {!embedded && (
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <Icon name="arrow_back" style={{ fontSize: 17 }} />
              Back
            </button>
            <div className={styles.divider} />
            <div className={styles.fileInfo}>
              <Icon name="view_in_ar" style={{ fontSize: 18, color: '#7dd3fc' }} />
              <span className={styles.fileName}>{fileInfo?.originalName ?? 'Loading…'}</span>
            </div>
          </div>
          <div className={styles.headerRight} />
        </header>
      )}

      <div className={styles.viewerBody}>
        {stage === 'ready' && cdnGlb !== undefined && (
          <iframe
            title="BIM viewer"
            src={`/bim-viewer.html?fileId=${encodeURIComponent(fileId!)}&${glbAuth}&v=10`}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          />
        )}

        {stage === 'idle' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <Icon name="view_in_ar" style={{ fontSize: 40, color: 'rgba(125,211,252,0.7)' }} />
            </div>
            <p className={styles.overlayTitle}>Prepare for Viewing</p>
            <p className={styles.overlayText}>
              This Revit model needs a one-time Autodesk conversion (RVT → IFC) before it can be
              viewed. After that it is meshed and served entirely by our own service.
            </p>
            <button className={styles.actionButton} style={{ marginTop: 12 }} onClick={startConversion}>
              <Icon name="rocket_launch" style={{ fontSize: 15 }} />
              Convert with Autodesk
            </button>
          </div>
        )}

        {(stage === 'checking' || stage === 'processing') && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <Icon name="sync" style={{ fontSize: 40, color: 'rgba(52,211,153,0.7)' }} />
            </div>
            <p className={styles.overlayTitle}>{stage === 'checking' ? 'Checking…' : 'Converting Model…'}</p>
            <p className={styles.overlayText}>
              {stage === 'checking' ? 'Checking whether this model is already converted.' : procText}
            </p>
            <div className={styles.progressBar}><div className={styles.progressFillIndeterminate} /></div>
          </div>
        )}

        {stage === 'notprepared' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <Icon name="view_in_ar" style={{ fontSize: 40, color: 'rgba(52,211,153,0.7)' }} />
            </div>
            <p className={styles.overlayTitle}>Not Prepared for Viewing</p>
            <p className={styles.overlayText}>
              This model has not been prepared for online viewing yet. Ask the project owner to open it once, or use the download option on the share page.
            </p>
          </div>
        )}

        {stage === 'failed' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <Icon name="error" style={{ fontSize: 40, color: 'rgba(248,113,113,0.7)' }} />
            </div>
            <p className={styles.overlayTitle}>Failed</p>
            <div className={styles.errorBox}>{error}</div>
            {!isShare && (
              <button className={styles.actionButton} style={{ marginTop: 12 }} onClick={startConversion}>
                <Icon name="refresh" style={{ fontSize: 15 }} />
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BIMViewerV2;
